import { useState, useEffect, useContext, useRef } from 'react';
import { WarehouseProduct } from '../models/WarehouseProduct';
import { WarehouseProductService, ProductSummary } from '../services/warehouseProductService';
import { UserContext } from '../context/userContext';

export const useWarehouseProduct = () => {
    const [products, setProducts] = useState<WarehouseProduct[]>([]);
    const [stockRemindProducts, setStockRemindProducts] = useState<WarehouseProduct[]>([]);
    const [productSummary, setProductSummary] = useState<ProductSummary | null>(null);
    const [productSummaryLoading, setProductSummaryLoading] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [totalProducts, setTotalProducts] = useState<number>(0);
    const { currentUser } = useContext(UserContext);
    const [hasMore, setHasMore] = useState<boolean>(true);
    const productListRef = useRef<HTMLDivElement>(null);
    const scrollPosition = useRef(0);
    const [isNewSearch, setIsNewSearch] = useState<boolean>(false); // Track new searches/filters

    const [initialLoadDone, setInitialLoadDone] = useState(false);

    const fetchAllFilteredProductsOffset = async (
        limit: number,
        offset: number,
        searchQuery: string,
        filterStartDate: string,
        filterEndDate: string,
        filterYear: string,
        filterWarehouseId: string
    ) => {
        setError(null);
        setLoading(true);

        try {
            let productData: WarehouseProduct[] = [];

            // For searches, fetch all matching products without limit/offset
            if (searchQuery || filterStartDate || filterEndDate || filterYear || filterWarehouseId) {
                const { products: rows, total } = await WarehouseProductService.getProductsPaginated(
                    1,
                    1000000,
                    searchQuery,
                    filterStartDate,
                    filterEndDate,
                    filterYear,
                    (currentUser?.warehouse_hq === "1") ? filterWarehouseId : currentUser?.warehouse_id?.toString()
                );
                setProducts(rows);
                setTotalProducts(total);
                setHasMore(false);
            }
            // For initial load and infinite scroll
            else {
                const { products: rows, total } = await WarehouseProductService.getProductsPaginated(
                    Math.floor(offset / limit) + 1,
                    limit,
                    '',
                    '',
                    '',
                    '',
                    (currentUser?.warehouse_hq === "1") ? '' : currentUser?.warehouse_id?.toString()
                );

                if (offset === 0) setProducts(rows); else setProducts(prev => [...prev, ...rows]);
                setTotalProducts(total);
                setHasMore(rows.length === limit);
            }

            if (offset === 0) {
                setInitialLoadDone(true);
            }
        } catch (err) {
            setError('Failed to fetch products');
        } finally {
            setLoading(false);
        }
    };

    const fetchAllFilteredProduct = async () => {
        setError(null);
        setLoading(true);

        try {

            if (currentUser?.warehouse_hq === "1") {
                const productData = await WarehouseProductService.getAllProducts();
                setProducts(productData);
            }
            else if (currentUser?.warehouse_id && currentUser.role!.toString() !== "1") {
                const productData = await WarehouseProductService.getAllProductsByWarehouseId(currentUser?.warehouse_id.toString());
                setProducts(productData);
            }

        } catch (err) {
            setError('Failed to fetch products');
        } finally {
            setLoading(false);
        }
    }

    // Superadmin always sees every warehouse regardless of whether their own user row happens to
    // have warehouse_hq set - a Superadmin isn't tied to any one warehouse, so warehouse_hq may
    // well be blank/null for that role. Falling through to "neither branch fires" (as happened
    // before this fix) silently left stockRemindProducts/productSummary empty for such users.
    const isHqView = (user: typeof currentUser) => user?.warehouse_hq === "1" || user?.role?.toString() === "1";

    const fetchAllFilteredProductsStockRemind = async () => {
        setLoading(true);
        setError(null);
        try {
            if (isHqView(currentUser)) {
                const productData = await WarehouseProductService.getAllProductsStockRemind();
                setStockRemindProducts(productData);
            } else if (currentUser?.warehouse_id) {
                const productData = await WarehouseProductService.getAllProductsStockRemindByWarehouseId(
                    currentUser.warehouse_id.toString()
                );
                setStockRemindProducts(productData);
            }
        } catch (err) {
            setError('Failed to fetch products');
        } finally {
            setLoading(false);
        }
    };

    const fetchProductSummary = async () => {
        setProductSummaryLoading(true);
        try {
            if (isHqView(currentUser)) {
                const summary = await WarehouseProductService.getProductSummary();
                setProductSummary(summary);
            } else if (currentUser?.warehouse_id) {
                const summary = await WarehouseProductService.getProductSummaryByWarehouseId(
                    currentUser.warehouse_id.toString()
                );
                setProductSummary(summary);
            }
        } catch (err) {
            setError('Failed to fetch product summary');
        } finally {
            setProductSummaryLoading(false);
        }
    };

    const fetchProductById = async (id: number) => {
        try {
            setLoading(true);
            const product = await WarehouseProductService.getProductById(id);
            return product;
        } catch (err) {
            setError('Failed to fetch this product');
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const createProduct = async (product: Omit<WarehouseProduct, 'id' | 'created_time' | 'updated_time'>) => {
        try {
            const newProductRes = await WarehouseProductService.createProduct(product);
            return newProductRes;
        } catch (err) {
            setError('Failed to add product');
        }
    };

    const updateProduct = async (id: number, updatedProduct: Omit<WarehouseProduct, 'created_time' | 'updated_time'>) => {
        try {
            const updatedProductRes = await WarehouseProductService.updateProduct(id, updatedProduct);
            return updatedProductRes;
        } catch (err) {
            setError('Failed to update product');
        }
    };

    const updateCurrentQuantity = async (id: number, quantity: number, action: string) => {
        try {
            const updatedProductRes = await WarehouseProductService.updateCurrentQuantity(id, quantity, action);
            return updatedProductRes;
        } catch (err) {
            setError('Failed to update product');
        }
    };

    const deleteProduct = async (id: number) => {
        setLoading(true);
        setError(null);
        try {
            await WarehouseProductService.deleteProduct(id);
            setProducts((prevProducts) => prevProducts.filter((i) => BigInt(i.id) !== BigInt(id)));
        } catch (err) {
            setError('Failed to delete product item');
        } finally {
            setLoading(false);
        }
    };

    // Automatically fetch products when the hook is first used
    useEffect(() => {
        fetchAllFilteredProductsStockRemind();
        fetchProductSummary();
    }, [currentUser]);

    return {
        products,
        stockRemindProducts,
        productSummary,
        productSummaryLoading,
        loading,
        error,
        createProduct,
        updateProduct,
        updateCurrentQuantity,
        deleteProduct,
        fetchProductById,
        fetchProductSummary,
        totalProducts,
        fetchAllFilteredProductsOffset,
        fetchAllFilteredProduct,
        hasMore,
        setIsNewSearch, // Expose setIsNewSearch to the component
    };
};